import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit3, Trash2, ShoppingBag, XCircle, Eye, Printer, Filter, ExternalLink, ChevronDown } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import Currency from '../Currency'
import { orderService, adminService } from '../../services/api'

const OrdersTab = () => {
  const navigate = useNavigate()
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Pagination states
  const [displayedOrders, setDisplayedOrders] = useState([])
  const [ordersToShow, setOrdersToShow] = useState(20) // Show 20 orders initially
  const ORDERS_PER_PAGE = 20

  const queryClient = useQueryClient()

  // Fetch orders using React Query
  const { data: ordersResponse, isLoading: ordersLoading, error: ordersError, refetch: fetchOrders } = useQuery({
    queryKey: ['admin-orders'],
    queryFn: () => adminService.getAllOrders(),
    retry: false,
  })

  const deleteOrderMutation = useMutation({
    mutationFn: (orderId) => orderService.deleteOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-orders'])
    }
  })

  const orders = ordersResponse?.orders || []

  // Filter orders based on status, city, and search term
  const filteredOrders = orders.filter((order) => {
    const matchesStatus = !statusFilter || order.status === statusFilter
    const matchesCity = !cityFilter || (order.city_name && order.city_name.toLowerCase().includes(cityFilter.toLowerCase()))
    const matchesSearch = !searchTerm || 
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.customer_name && order.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.customer_email && order.customer_email.toLowerCase().includes(searchTerm.toLowerCase()))
    
    return matchesStatus && matchesCity && matchesSearch
  })

  // Update displayed orders when filters change
  useEffect(() => {
    setOrdersToShow(ORDERS_PER_PAGE) // Reset to initial page size when filters change
    setDisplayedOrders(filteredOrders.slice(0, ORDERS_PER_PAGE))
  }, [filteredOrders])

  // Update displayed orders when ordersToShow changes
  useEffect(() => {
    setDisplayedOrders(filteredOrders.slice(0, ordersToShow))
  }, [ordersToShow])

  // Load more orders function
  const loadMoreOrders = () => {
    setOrdersToShow(prev => prev + ORDERS_PER_PAGE)
  }

  // Check if there are more orders to load
  const hasMoreOrders = ordersToShow < filteredOrders.length

  // Get unique cities for filter dropdown
  const uniqueCities = [...new Set(orders.map(order => order.city_name).filter(Boolean))].sort()

  const handleViewOrderDetails = (order) => {
    window.open(`/admin/orders/${order.id}`, '_blank')
  }

  const handlePrintInvoice = (order) => {
    // Open invoice page in new tab with auto-print parameter
    window.open(`/admin/orders/${order.id}/invoice?autoprint=true`, '_blank')
  }

  // Helper functions
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <XCircle className="h-4 w-4" />
      case 'processing':
        return <Eye className="h-4 w-4" />
      case 'shipped':
        return <Printer className="h-4 w-4" />
      case 'delivered':
        return <ShoppingBag className="h-4 w-4" />
      default:
        return <XCircle className="h-4 w-4" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'processing':
        return 'text-blue-600 bg-blue-100'
      case 'shipped':
        return 'text-purple-600 bg-purple-100'
      case 'delivered':
        return 'text-green-600 bg-green-100'
      case 'cancelled':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const updateOrderStatusMutation = useMutation({
    mutationFn: ({ orderId, status }) => orderService.updateOrderStatus(orderId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-orders'])
    }
  })

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await updateOrderStatusMutation.mutateAsync({ orderId, status })
      console.log('Order status updated successfully')
    } catch (error) {
      console.error('Failed to update order status:', error)
    }
  }

  const deleteOrder = async (orderId) => {
    deleteOrderMutation.mutate(orderId)
  }

  const handleDeleteOrder = (orderId, orderNumber) => {
    if (window.confirm(`هل أنت متأكد من حذف الطلب #${orderNumber}؟ هذا الإجراء لا يمكن التراجع عنه.`)) {
      deleteOrder(orderId)
    }
  }
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">الطلبات</h2>
        <button
          onClick={fetchOrders}
          disabled={ordersLoading}
          className="btn btn-outline btn-sm"
        >
          {ordersLoading ? <LoadingSpinner size="sm" /> : 'تحديث'}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg border p-4">
        <div className="flex items-center space-x-4 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">المرشحات</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              البحث
            </label>
            <input
              type="text"
              placeholder="رقم الطلب، اسم العميل، البريد الإلكتروني..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input w-full"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              الحالة
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-full"
            >
              <option value="">جميع الحالات</option>
              <option value="pending">معلق</option>
              <option value="processing">قيد المعالجة</option>
              <option value="shipped">تم الشحن</option>
              <option value="delivered">تم التسليم</option>
              <option value="cancelled">ملغي</option>
              <option value="refunded">مسترد</option>
            </select>
          </div>

          {/* City Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              المدينة
            </label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="input w-full"
            >
              <option value="">جميع المدن</option>
              {uniqueCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>

          {/* Results Count */}
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              عرض {filteredOrders.length} من {orders.length} طلب
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white shadow rounded-lg border">
        <div className="px-6 py-4">
          {ordersLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : ordersError ? (
            <div className="text-center py-8">
              <XCircle className="mx-auto h-12 w-12 text-red-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">خطأ في تحميل الطلبات</h3>
              <p className="mt-1 text-sm text-gray-500">{ordersError?.message || 'فشل في تحميل الطلبات'}</p>
              <button
                onClick={fetchOrders}
                className="mt-4 btn btn-outline btn-sm"
              >
                إعادة المحاولة
              </button>
            </div>
          ) : !filteredOrders || filteredOrders.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {orders.length === 0 ? 'لا توجد طلبات بعد' : 'لا توجد طلبات تطابق المرشحات'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {orders.length === 0 
                  ? 'ستظهر الطلبات هنا عندما يقوم العملاء بالشراء.'
                  : 'جرب تعديل المرشحات لرؤية المزيد من النتائج.'
                }
              </p>
            </div>
          ) : (
            <div>
              {/* Horizontal scroll hint */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center text-sm text-blue-800">
                  <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  يمكن التمرير أفقياً لرؤية المزيد من التفاصيل في الجدول أدناه
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        رقم الطلب
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        العميل
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        الاسم
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        المدينة
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        التاريخ
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        الحالة
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        الشحن
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        الإجمالي
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayedOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">
                            #{order.order_number}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {order.id}
                          </div>
                          {/* Mobile-only additional info - simplified */}
                          <div className="md:hidden text-xs text-gray-500 mt-1">
                            <div>{parseFloat(order.total_amount || 0).toFixed(3)} د.ك</div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                              {order.status_display || order.status}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm font-medium text-gray-900">
                          {order.customer_type || (order.user_id ? 'User' : 'Guest')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">
                            {order.customer_name || order.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.customer_email || order.email || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                        {order.city_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                        {new Date(order.created_at).toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {order.status_display || order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                        {parseFloat(order.shipping_amount || 0).toFixed(3)} د.ك
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 hidden md:table-cell">
                        {parseFloat(order.total_amount || 0).toFixed(3)} د.ك
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col md:flex-row md:items-center md:space-x-2 space-y-1 md:space-y-0">
                          <button
                            onClick={() => handleViewOrderDetails(order)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 flex items-center justify-center"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => handlePrintInvoice(order)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 flex items-center justify-center"
                            title="Print Invoice"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          
                          <select
                            value={order.status}
                            onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded-md px-2 py-1 w-full md:w-auto"
                            title="تحديث الحالة"
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">ملغي</option>
                            <option value="refunded">Refunded</option>
                          </select>
                          
                          <button
                            onClick={() => handleDeleteOrder(order.id, order.order_number)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                            title="حذف الطلب"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load More Button */}
            {hasMoreOrders && (
              <div className="mt-6 text-center">
                <div className="mb-4 text-sm text-gray-600">
                  عرض {displayedOrders.length} من {filteredOrders.length} طلب
                </div>
                <button
                  onClick={loadMoreOrders}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <ChevronDown className="w-4 h-4 ml-2" />
                  تحميل المزيد ({filteredOrders.length - ordersToShow} طلب متبقي)
                </button>
              </div>
            )}
            
            {/* Show total when all are loaded */}
            {!hasMoreOrders && filteredOrders.length > ORDERS_PER_PAGE && (
              <div className="mt-6 text-center text-sm text-gray-600">
                تم عرض جميع الطلبات ({filteredOrders.length} طلب)
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrdersTab
